import {
  getEventsDescription,
  getSequenceDescription,
  getSequenceTitle,
  PRESENTATION_EVENTS,
} from '@/components/modules/option/bar-sequence/utils/strings';
import { MODE_TRANSFER, MODE_WATER } from '@/lib/constants';
import { LocationDictionaryModel } from '@/lib/models/location/types';
import { OptionModel } from '@/lib/models/option/types';
import { SegmentLinkModel, SegmentTransferModel } from '@/lib/models/segment/types';
import {
  RouteLinkSegmentEventApiV4Type,
  RouteLinkSegmentEventListApiV4Interface,
  SegmentModeType,
} from '@/types/api-types';
import { generateId } from '@/utils/helpers';

export interface SequenceItem {
  id: string;
  fromLocationId: string;
  mode: SegmentModeType;
  eventType?: RouteLinkSegmentEventApiV4Type;
  isStop: boolean;
  durationSeconds: number;
  title: string;
  description: string;
  hasEvents: boolean;
  startTimeSeconds: number;
  shouldShowIcon?: boolean;
  percentage: number;
  isSequenceStart: boolean;
  isSequenceEnd: boolean;
}

// Will filter for event types that are relevant to a bar sequence
const getFilteredEvents = (events: RouteLinkSegmentEventListApiV4Interface[]) => {
  return events.flat().filter((event) => (PRESENTATION_EVENTS as ReadonlyArray<string>).includes(event.type));
};

export const getOptionSequenceItems = ({
  option,
  locations,
}: {
  option: OptionModel;
  locations: LocationDictionaryModel;
}): SequenceItem[] => {
  const { segments, totalDurationSeconds } = option;

  return segments.reduce((acc, segment) => {
    if (segment.routeType === MODE_TRANSFER) {
      return [...acc, handleTransfer({ segment, locations, totalDurationSeconds })];
    }

    if (segment.routeType !== MODE_WATER) {
      return [...acc, handleDefault({ segment: segment as SegmentLinkModel, locations, totalDurationSeconds })];
    }

    if ('events' in segment) {
      const sequenceItems = getSegmentSequenceItems({
        segment,
        totalDurationSeconds,
        locations,
      });

      return [...acc, ...sequenceItems];
    }

    return [...acc, handleDefault({ segment: segment as SegmentLinkModel, locations, totalDurationSeconds })];
  }, [] as SequenceItem[]);
};

const getPercent = ({
  durationSeconds = 0,
  totalDurationSeconds = 1,
}: {
  durationSeconds?: number;
  totalDurationSeconds?: number;
}): number => {
  return (durationSeconds / totalDurationSeconds) * 100;
};

const handleTransfer = ({
  segment,
  totalDurationSeconds,
  locations,
}: {
  segment: SegmentTransferModel;
  totalDurationSeconds: number;
  locations: LocationDictionaryModel;
}): SequenceItem => {
  const mainTitle = getSequenceTitle(segment, locations);
  const segmentDescription = getSequenceDescription(
    {
      fromId: segment.fromId,
    },
    locations,
  );
  const percentage = getPercent({ durationSeconds: segment.durationSeconds, totalDurationSeconds });

  return {
    id: segment.id,
    title: mainTitle,
    description: segmentDescription,
    isStop: false,
    percentage,
    hasEvents: false,
    fromLocationId: segment.fromId,
    mode: segment.routeType,
    startTimeSeconds: 0,
    durationSeconds: segment.durationSeconds,
    isSequenceEnd: true,
    isSequenceStart: true,
    shouldShowIcon: false,
  };
};

const handleDefault = ({
  segment,
  totalDurationSeconds,
  locations,
}: {
  segment: SegmentLinkModel;
  totalDurationSeconds: number;
  locations: LocationDictionaryModel;
}): SequenceItem => {
  const events = getFilteredEvents(segment.events);
  const hasEvents = events.length !== 0;
  const eventsDescription = hasEvents ? getEventsDescription({ events }, locations) : '';
  const mainTitle = getSequenceTitle(segment, locations);
  const segmentDescription = getSequenceDescription(
    {
      fromId: segment.fromId,
      toId: segment.toId,
    },
    locations,
  );

  return {
    id: segment.id,
    title: mainTitle,
    description: `${segmentDescription}${eventsDescription}`,
    isStop: false,
    percentage: getPercent({ durationSeconds: segment.durationSeconds, totalDurationSeconds }),
    hasEvents,
    fromLocationId: segment.fromId,
    mode: segment.routeType,
    startTimeSeconds: 0,
    durationSeconds: segment.durationSeconds,
    isSequenceEnd: true,
    isSequenceStart: true,
    shouldShowIcon: true,
  };
};

/**
 * Will create a sequence item for each event in a segment
 */
const getSegmentSequenceItems = ({
  segment,
  totalDurationSeconds,
  locations,
}: {
  segment: SegmentLinkModel;
  totalDurationSeconds: number;
  locations: LocationDictionaryModel;
}): SequenceItem[] => {
  const baseSequenceItems = getFilteredEvents(segment.events).map((event): SequenceItem => {
    const eventTitle = getSequenceTitle(
      { fromId: event.locationId, routeType: MODE_TRANSFER, transferType: event.type },
      locations,
    );
    const eventDescription = getSequenceDescription({ fromId: event.locationId }, locations);

    return {
      id: generateId(),
      isStop: true,
      title: eventTitle,
      description: eventDescription,
      durationSeconds: event.durationSeconds || 0,
      hasEvents: false,
      fromLocationId: event.locationId,
      mode: segment.routeType,
      startTimeSeconds: event.startTimeSeconds || 0,
      percentage: getPercent({ durationSeconds: event.durationSeconds, totalDurationSeconds }),
      isSequenceStart: false,
      isSequenceEnd: false,
      eventType: event.type,
    };
  });

  const allSequenceItems = fillSequenceGaps({
    sequenceItems: baseSequenceItems,
    segmentMode: segment.routeType,
    locations,
    totalDurationSeconds,
  });

  if (allSequenceItems.length) {
    return insertSequenceItemsInSegment({ segment, sequenceItems: allSequenceItems, totalDurationSeconds, locations });
  }
  return [handleDefault({ segment, totalDurationSeconds, locations })];
};

/**
 * Fills the gaps between sequence items by adding new items.
 * Consider we have two event sequence items that have some non-zero amount of
 * time between them:
 * We start off with this:       [PER (stop)], [SGSIN (stop)]
 * We add the missing
 * sequence item to indicate a
 * leg between PER and SGSIN:    [PER (stop)], [PER]->[SGSIN], [SGSIN (stop)]
 */
const fillSequenceGaps = ({
  sequenceItems,
  locations,
  segmentMode,
  totalDurationSeconds,
}: {
  sequenceItems: SequenceItem[];
  totalDurationSeconds: number;
  segmentMode: SegmentModeType;
  locations: LocationDictionaryModel;
}): SequenceItem[] => {
  return sequenceItems.reduce((sequenceAcc, sequenceItem, i): SequenceItem[] => {
    if (i === sequenceItems.length - 1) {
      return [...sequenceAcc, sequenceItem];
    }

    const nextSequenceItem = sequenceItems[i + 1];
    const sequenceEndTime = sequenceItem.startTimeSeconds + sequenceItem.durationSeconds;
    if (sequenceEndTime !== nextSequenceItem.startTimeSeconds) {
      const durationSeconds = nextSequenceItem.startTimeSeconds - sequenceEndTime;
      const midSequenceTitle = getSequenceTitle(
        {
          fromId: sequenceItem.fromLocationId,
          toId: nextSequenceItem.fromLocationId,
          routeType: segmentMode,
          transferType: sequenceItem.eventType,
        },
        locations,
      );
      const midSequenceDescription = getSequenceDescription(
        {
          fromId: sequenceItem.fromLocationId,
          toId: nextSequenceItem.fromLocationId,
        },
        locations,
      );

      return [
        ...sequenceAcc,
        sequenceItem,
        {
          id: generateId(),
          title: midSequenceTitle,
          description: midSequenceDescription,
          startTimeSeconds: sequenceEndTime,
          durationSeconds,
          isStop: false,
          hasEvents: false,
          mode: segmentMode,
          fromLocationId: sequenceItem.fromLocationId,
          percentage: getPercent({ durationSeconds, totalDurationSeconds }),
          isSequenceStart: false,
          isSequenceEnd: false,
        },
      ];
    }

    return [...sequenceAcc, sequenceItem];
  }, [] as SequenceItem[]);
};

/**
 * Will create a start and end sequence item based on the segment, and insert
 * the "event" sequence items in the middle.
 * Consider a route from Melbourne to Dubai that has a stop in Perth and
 * Singapore in a single segment:
 * The segment looks like:        [MEL]->[DXB]
 * And the event sequence for
 * the segment:                   [PER (stop)], [PER]->[SGSIN], [SGSIN (stop)]
 * Then we add new sequence items
 * from the sequence start to the
 * first event location, and the
 * last event destination and the
 * sequence end location:         [MEL]->[PER], [PER (stop)], [PER]->[SGSIN], [SGSIN (stop)], [SGSIN]->[DXB]
 */
const insertSequenceItemsInSegment = ({
  sequenceItems,
  segment,
  totalDurationSeconds,
  locations,
}: {
  sequenceItems: SequenceItem[];
  segment: SegmentLinkModel;
  totalDurationSeconds: number;
  locations: LocationDictionaryModel;
}): SequenceItem[] => {
  const firstSequenceItem = sequenceItems[0];
  const lastSequenceItem = sequenceItems[sequenceItems.length - 1];
  const lastStartTime = lastSequenceItem.startTimeSeconds + lastSequenceItem.durationSeconds;
  const lastDurationSeconds = segment.durationSeconds - lastStartTime;

  const startSequenceTitle = getSequenceTitle(
    {
      fromId: segment.fromId,
      toId: firstSequenceItem.fromLocationId,
      routeType: segment.routeType,
    },
    locations,
  );
  const startSequenceDescription = getSequenceDescription(
    {
      fromId: segment.fromId,
      toId: firstSequenceItem.fromLocationId,
    },
    locations,
  );

  const endSequenceTitle = getSequenceTitle(
    {
      fromId: lastSequenceItem.fromLocationId,
      toId: segment.toId,
      routeType: segment.routeType,
    },
    locations,
  );
  const endSequenceDescription = getSequenceDescription(
    {
      fromId: lastSequenceItem.fromLocationId,
      toId: segment.toId,
    },
    locations,
  );

  return [
    {
      id: segment.id,
      title: startSequenceTitle,
      description: startSequenceDescription,
      mode: segment.routeType,
      startTimeSeconds: 0,
      durationSeconds: firstSequenceItem.startTimeSeconds,
      fromLocationId: segment.fromId,
      isStop: false,
      hasEvents: false,
      percentage: getPercent({ durationSeconds: firstSequenceItem.startTimeSeconds, totalDurationSeconds }),
      isSequenceStart: true,
      isSequenceEnd: false,
      shouldShowIcon: true,
    },
    ...sequenceItems,
    {
      id: generateId(),
      title: endSequenceTitle,
      description: endSequenceDescription,
      hasEvents: false,
      isStop: false,
      fromLocationId: lastSequenceItem.fromLocationId,
      startTimeSeconds: lastStartTime,
      durationSeconds: lastDurationSeconds,
      mode: segment.routeType,
      percentage: getPercent({ durationSeconds: lastDurationSeconds, totalDurationSeconds }),
      isSequenceStart: false,
      isSequenceEnd: true,
    },
  ];
};
